import { Box, Button, Flex, HStack, Image, Text } from "@chakra-ui/react";
import { useDisclosure } from "@chakra-ui/react";
import { useMemo } from "react";
import { ThemeColor } from "../../theme/palette";
import { DripTokenMeta } from "./dripStyles";
import {
  buildDripRewardAssetOptions,
  DripRewardAssetSelectModal,
} from "./DripRewardAssetSelectModal";
import { DRIP_SEGMENT_ACTIVE_BG, DRIP_TEXT_MUTED, DRIP_TRACK_BG } from "./dripStyles";

const MAX_SEGMENTED_ASSETS = 4;
const TOP_TAB_COUNT = 3;

interface DripRewardAssetSelectorProps {
  assetMints: string[];
  activeMint: string;
  onSelectMint: (mint: string) => void;
  tokenMetaByMint: Record<string, DripTokenMeta>;
  totalsByMint: Record<string, string>;
  decimalsByMint: Record<string, number>;
  resolveLabel: (mint: string) => string;
}

interface AssetTabProps {
  isActive: boolean;
  label: string;
  image?: string;
  onClick: () => void;
  flex?: number | string;
}

const AssetTab = ({ isActive, label, image, onClick, flex = 1 }: AssetTabProps) => (
  <Button
    type="button"
    flex={flex}
    variant="unstyled"
    display="flex"
    alignItems="center"
    justifyContent="center"
    borderRadius="lg"
    py={2}
    px={3}
    minH="36px"
    minW={0}
    bg={isActive ? DRIP_SEGMENT_ACTIVE_BG : "transparent"}
    boxShadow={isActive ? "inset 0 0 0 1px rgba(95, 244, 244, 0.35)" : "none"}
    onClick={onClick}
    _hover={{ bg: isActive ? DRIP_SEGMENT_ACTIVE_BG : "whiteAlpha.100" }}
  >
    <HStack spacing={2} minW={0}>
      {image && <Image src={image} boxSize="16px" borderRadius="full" flexShrink={0} />}
      <Text
        fontSize="sm"
        fontWeight={isActive ? "semibold" : "medium"}
        color={isActive ? ThemeColor.KAIROS : DRIP_TEXT_MUTED}
        noOfLines={1}
      >
        {label}
      </Text>
    </HStack>
  </Button>
);

export const DripRewardAssetSelector = ({
  assetMints,
  activeMint,
  onSelectMint,
  tokenMetaByMint,
  totalsByMint,
  decimalsByMint,
  resolveLabel,
}: DripRewardAssetSelectorProps) => {
  const { isOpen, onOpen, onClose } = useDisclosure();

  const assetOptions = useMemo(
    () => buildDripRewardAssetOptions(assetMints, totalsByMint, tokenMetaByMint, decimalsByMint),
    [assetMints, totalsByMint, tokenMetaByMint, decimalsByMint]
  );

  if (assetMints.length <= 1) return null;

  const useOverflow = assetMints.length > MAX_SEGMENTED_ASSETS;
  const tabMints = useOverflow ? assetMints.slice(0, TOP_TAB_COUNT) : assetMints;
  const isOverflowSelection = useOverflow && !tabMints.includes(activeMint);
  const overflowCount = useOverflow ? assetMints.length - TOP_TAB_COUNT : 0;

  const overflowLabel = isOverflowSelection
    ? resolveLabel(activeMint)
    : `+${overflowCount} more`;

  const overflowImage = isOverflowSelection ? tokenMetaByMint[activeMint]?.image : undefined;

  return (
    <>
      <Box
        bg={DRIP_TRACK_BG}
        borderRadius="xl"
        border="1px solid"
        borderColor="whiteAlpha.200"
        p="3px"
        width="100%"
      >
        <Flex gap="3px">
          {tabMints.map((mint) => (
            <AssetTab
              key={mint}
              isActive={mint === activeMint}
              label={resolveLabel(mint)}
              image={tokenMetaByMint[mint]?.image}
              onClick={() => onSelectMint(mint)}
            />
          ))}
          {useOverflow && (
            <AssetTab
              key="overflow"
              isActive={isOverflowSelection}
              label={overflowLabel}
              image={overflowImage}
              onClick={onOpen}
            />
          )}
        </Flex>
      </Box>

      {useOverflow && (
        <DripRewardAssetSelectModal
          isOpen={isOpen}
          onClose={onClose}
          options={assetOptions}
          selectedMint={activeMint}
          onSelect={onSelectMint}
        />
      )}
    </>
  );
};
